//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React, { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import styled from 'styled-components';

import { FileUploader } from '@campaigns/components/shared/file-uploader/FileUploader';
import { FormField } from '@campaigns/components/shared/product-details-modal/form/FormField';
import { MAX_IMAGE_SIZE_BYTES } from '@campaigns/constants/SamplingImageConstants';
import { SAMPLING_CAMPAIGNS_TRACKING_ID } from '@campaigns/constants/sampling/SamplingCampaignsTrackingId';
import {
  URL_REGEX_RULE,
  isValidImageUrl,
} from '@campaigns/constants/sampling/SamplingProductValidationRulesConstants';
import { useProductModal } from '@campaigns/contexts/ProductModalContext';
import { useImageUpload } from '@campaigns/hooks/useImageUpload';
import { EditableBVSamplingProduct } from '@campaigns/types/sampling/SamplingProductTypes';
import { Alert } from '@components/atoms/alert/Alert';
import {
  PrimaryButton,
  PrimaryLinkButton,
} from '@components/atoms/button/Button';
import Icon from '@components/atoms/icon/Icon';
import { BodyText } from '@components/atoms/text/Text';
import { isDefined } from '@utils/filterPredicates';
import { SUPPORTED_IMAGE_MIME_TYPES } from '@utils/mimeTypes';

interface ProductImageUploadProps {
  isViewOnly: boolean;
  isDisabled: boolean;
  onFileSelect: (file: File | undefined) => void;
  onClearImage: () => void;
  onPreview: () => void;
  updateVariantImages: (url: string) => void;
  error?: string;
}

export const ProductImageUpload = ({
  error,
  isDisabled = false,
  isViewOnly = false,
  onClearImage,
  onFileSelect,
  onPreview,
  updateVariantImages,
}: ProductImageUploadProps) => {
  const { isImageForAllVariants } = useProductModal();
  const { watch } = useFormContext<EditableBVSamplingProduct>();
  const [isImageUrlInputMode, setIsImageUrlInputMode] = useState(false);
  const formImgUrl = watch('imgUrl');

  const { error: imageUploadError, handleFilesSelected } = useImageUpload({
    onClearImage,
    onFileSelect,
  });

  const displayError = error ?? imageUploadError;

  const toggleInputMode = () => {
    setIsImageUrlInputMode(!isImageUrlInputMode);
    onClearImage();
  };

  return (
    <Container>
      {isImageUrlInputMode ? (
        <UrlInputContainer>
          <FormField
            inputType='textarea'
            isViewOnly={isViewOnly}
            label='Image web address'
            name='imgUrl'
            onValueChange={(value: string) => {
              if (isValidImageUrl(value) && isImageForAllVariants) {
                updateVariantImages(value);
              }
            }}
            placeholder='https://your-awesome-website.com/030524.png'
            rules={{
              required: {
                value: true,
                message: 'Please enter a product image URL',
              },
              pattern: {
                value: URL_REGEX_RULE,
                message: 'Please enter a valid URL',
              },
            }}
          />
          <PreviewButton
            isDisabled={!isValidImageUrl(formImgUrl)}
            onClick={onPreview}
            trackingId={
              SAMPLING_CAMPAIGNS_TRACKING_ID.PRODUCT_MODAL_PREVIEW_IMAGE_BUTTON
            }
          >
            Preview
          </PreviewButton>
        </UrlInputContainer>
      ) : (
        <>
          {/*
            This form can switch between two input modes: file upload and image URL textarea.
            React Hook Form loses validation state for a field if it is unmounted from the DOM.
            To ensure validation errors for the image URL persist and display correctly when switching between modes,
            we keep the textarea field for 'imgUrl' hidden (but mounted) when using file upload.
            This allows error messages to be shown consistently, regardless of which input method is active.
          */}
          <HiddenFormField>
            <FormField
              inputType='textarea'
              label='Image web address'
              name='imgUrl'
              rules={{
                required: {
                  value: true,
                  message: 'Please upload a product image',
                },
                validate: {
                  validUrl: (value) =>
                    !value ||
                    isValidImageUrl(value) ||
                    'Please provide a valid image URL',
                },
              }}
            />
          </HiddenFormField>
          {isDefined(displayError) && (
            <Alert type='error'>{displayError}</Alert>
          )}
          <FileUploader
            actionText='or click to select'
            existingAssetUrls={[]}
            isDisabled={isDisabled}
            maxTotalSize={MAX_IMAGE_SIZE_BYTES}
            onFilesSelected={handleFilesSelected}
            shouldAcceptMultipleFiles={false}
            supportedFileTypes={SUPPORTED_IMAGE_MIME_TYPES}
            title='Upload Product Image'
            trackingId={
              SAMPLING_CAMPAIGNS_TRACKING_ID.PRODUCT_MODAL_INPUT_FIELD_UPLOAD_IMAGE
            }
          />
        </>
      )}
      {!isViewOnly && (
        <ToggleContainer>
          <BodyText>or</BodyText>
          {isImageUrlInputMode ? (
            <PrimaryLinkButton
              onClick={toggleInputMode}
              trackingId={
                SAMPLING_CAMPAIGNS_TRACKING_ID.PRODUCT_MODAL_UPLOAD_IMAGE_TOGGLE_BUTTON
              }
            >
              <StyledIcon name='upload' />
              <span>Upload an image</span>
            </PrimaryLinkButton>
          ) : (
            <PrimaryLinkButton
              onClick={toggleInputMode}
              trackingId={
                SAMPLING_CAMPAIGNS_TRACKING_ID.PRODUCT_MODAL_IMAGE_URL_TOGGLE_BUTTON
              }
            >
              Enter image URL instead
            </PrimaryLinkButton>
          )}
        </ToggleContainer>
      )}
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacings[2]};
`;

const UrlInputContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacings[1]};
`;

const ToggleContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacings[2]};
`;

const StyledIcon = styled(Icon)`
  margin-right: ${({ theme }) => theme.spacings[1]};
  vertical-align: middle;
`;

const PreviewButton = styled(PrimaryButton)`
  margin-top: ${({ theme }) => theme.spacings[2]};
`;

const HiddenFormField = styled.div`
  display: none;
`;
